import {
	fetchPriceCallHistory,
	formatCallTimestamp,
	type PriceCallRecord,
} from "@/lib/oracle/oracle-autonomy";
import { formatNum } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

const PAGE_SIZE = 15;

function resultCell(row: PriceCallRecord): string {
	if (row.ok && row.priceE8 != null) {
		const usd = Number(row.priceE8) / 1e8;
		const dec = row.asset === "ICP" ? 4 : 2;
		return `$${formatNum(usd, dec)}`;
	}
	if (!row.ok && row.error) {
		return row.error.length > 48 ? `${row.error.slice(0, 48)}…` : row.error;
	}
	return row.ok ? "ok" : "err";
}

/** On-chain paginated history of oracle price checks (XRC, aggregate, admin). */
export function PriceCallHistoryPanel() {
	const [page, setPage] = useState(0);
	const offset = page * PAGE_SIZE;

	const { data, isLoading, isError, isFetching, refetch } = useQuery({
		queryKey: ["v2", "oraclePriceCallHistory", offset, PAGE_SIZE],
		queryFn: () => fetchPriceCallHistory(offset, PAGE_SIZE),
		staleTime: 30_000,
		refetchInterval: 60_000,
		retry: 1,
	});

	const total = data?.total ?? 0;
	const entries = data?.entries ?? [];
	const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
	const hasNext = offset + PAGE_SIZE < total;

	return (
		<section className="card-surface space-y-4 p-4 sm:p-5">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<p className="section-eyebrow">Call history</p>
					<h2 className="text-lg font-bold">Oracle price checks</h2>
					<p className="mt-1 max-w-2xl text-sm text-muted-foreground">
						On-chain log of each price attempt: date/time (to the second),
						asset, source (XRC, external aggregate, admin), and result. Ring
						buffer keeps the latest ~1,000 checks.
					</p>
				</div>
				<button
					type="button"
					className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/40 disabled:opacity-50"
					disabled={isFetching}
					onClick={() => void refetch()}
				>
					{isFetching ? "Refreshing…" : "Refresh"}
				</button>
			</div>

			{isLoading ? (
				<div className="skeleton-shimmer h-40 rounded-xl" />
			) : isError || data === null ? (
				<p className="text-sm text-muted-foreground">
					History unavailable (oracle env missing or canister not upgraded with{" "}
					<span className="font-mono-amount">get_price_call_history</span>).
				</p>
			) : total === 0 ? (
				<p className="text-sm text-muted-foreground">
					No checks recorded yet. History fills after the next auto-refresh,
					keeper aggregate, or manual price update.
				</p>
			) : (
				<>
					<div className="overflow-x-auto rounded-xl border border-border/60">
						<table className="w-full min-w-[640px] text-left text-sm">
							<thead className="border-b border-border bg-muted/30 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
								<tr>
									<th className="px-3 py-2.5">Date · time</th>
									<th className="px-3 py-2.5">Asset</th>
									<th className="px-3 py-2.5">Source</th>
									<th className="px-3 py-2.5">Status</th>
									<th className="px-3 py-2.5">Result</th>
									<th className="px-3 py-2.5">Detail</th>
								</tr>
							</thead>
							<tbody>
								{entries.map((row, i) => (
									<tr
										key={`${row.tsNs.toString()}-${row.asset}-${i}`}
										className="border-b border-border/40 last:border-0"
									>
										<td className="whitespace-nowrap px-3 py-2 font-mono text-xs">
											{formatCallTimestamp(row.tsNs)}
										</td>
										<td className="px-3 py-2 font-semibold">{row.asset}</td>
										<td className="px-3 py-2 font-mono text-xs text-muted-foreground">
											{row.source}
										</td>
										<td className="px-3 py-2">
											<span
												className={
													row.ok
														? "rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary"
														: "rounded-full bg-danger/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-danger"
												}
											>
												{row.ok ? "ok" : "err"}
											</span>
										</td>
										<td
											className="max-w-[12rem] truncate px-3 py-2 font-mono text-xs"
											title={resultCell(row)}
										>
											{resultCell(row)}
										</td>
										<td
											className="max-w-[10rem] truncate px-3 py-2 text-xs text-muted-foreground"
											title={
												row.detail ||
												(row.numSources > 0n
													? `${row.numSources.toString()} sources`
													: "")
											}
										>
											{row.detail ||
												(row.numSources > 0n
													? `${row.numSources.toString()} src`
													: "—")}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					<div className="flex items-center justify-between gap-2 pt-1">
						<button
							type="button"
							disabled={page <= 0}
							onClick={() => setPage((p) => Math.max(0, p - 1))}
							className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
						>
							Previous
						</button>
						<span className="text-[11px] text-muted-foreground">
							Page {page + 1}
							{total > 0 ? ` of ${pageCount}` : ""} · {total} total
						</span>
						<button
							type="button"
							disabled={!hasNext}
							onClick={() => setPage((p) => p + 1)}
							className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
						>
							Next
						</button>
					</div>
				</>
			)}
		</section>
	);
}
